import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import Sidebar from './components/Sidebar';
import RequireAuth from './components/RequireAuth';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { StatusProvider } from './context/StatusContext';
import { RealtimeProvider } from './context/RealtimeContext';
import SupportBotEmbed from './components/SupportBotEmbed';
import AppErrorBoundary from './components/AppErrorBoundary';
import useChangelogNotifications from './hooks/useChangelogNotifications';
import HomePage from './pages/HomePage';
import BotsPage from './pages/BotsPage';
import ChatHistoryPage from './pages/ChatHistoryPage';
import HenvendelserOverviewPage from './pages/HenvendelserOverviewPage';
import InquiriesPage from './pages/InquiriesPage.js';
import InquiryDetailPage from './pages/InquiryDetailPage.js';
import InquiryCalendarPage from './pages/InquiryCalendarPage.js';
import InquiryTasksPage from './pages/InquiryTasksPage.js';
// InstallationPage moved to bot configuration - import removed
// KnowledgeBasePage moved to bot configuration - import removed
// BillingPage merged into SubscriptionPage
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
import UserProfilePage from './pages/UserProfilePage';
import BookingPage from './pages/BookingPage';
import MembersPage from './pages/MembersPage';
import AccessPage from './pages/AccessPage';
import RegisterPage from './pages/RegisterPage';
import CompanyOverviewPage from './pages/CompanyOverviewPage';
import PartnerInviteCompanyPage from './pages/PartnerInviteCompanyPage';
import CompanyPage from './pages/CompanyPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import GoogleCalendarPage from './pages/GoogleCalendarPage';
import SuperAdminCompaniesPage from './pages/SuperAdminCompaniesPage';
import SubscriptionPage from './pages/SubscriptionPage';
import AddonsPage from './pages/AddonsPage';
import AgentPage from './pages/AgentPage';
import FormsPage from './pages/FormsPage';
import InquiryTypesPage from './pages/InquiryTypesPage';
import ProductsPage from './pages/ProductsPage';
import ProductEditPage from './pages/ProductEditPage';
import ChangelogPage from './pages/ChangelogPage';
// SchedulesPage removed - schedule config is now in BotSchedulesPage (Support-tidsplan)
import './App.css';

function DashboardLayout() {
  const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  useChangelogNotifications();

  useEffect(() => {
    if (!isMobileSidebarOpen) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileSidebarOpen]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 901px)');
    const handleChange = (event) => {
      if (event.matches) {
        setMobileSidebarOpen(false);
      }
    };
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
    mediaQuery.addListener(handleChange);
    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  const isFlowStudio = location.pathname.startsWith('/bots/flows/studio');
  const isInquiriesPage = location.pathname === '/henvendelser'
    || location.pathname === '/henvendelser/innboks'
    || location.pathname === '/henvendelser/oppgaver'
    || location.pathname === '/henvendelser/kalender';

  useEffect(() => {
    const shouldCollapse = isFlowStudio;
    setSidebarCollapsed(shouldCollapse);
  }, [isFlowStudio]);

  const handleToggleSidebar = () => {
    setMobileSidebarOpen((open) => !open);
  };

  const handleCloseSidebar = () => {
    setMobileSidebarOpen(false);
  };

  return (
    <div className="app">
      <Sidebar
        isMobileOpen={isMobileSidebarOpen}
        isCollapsed={isSidebarCollapsed && !isMobileSidebarOpen}
        onClose={handleCloseSidebar}
        onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
      />
      {isMobileSidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          onClick={handleCloseSidebar}
          aria-label="Lukk meny"
        />
      )}
      <main className={`main-content ${isFlowStudio ? 'main-content--flush' : ''}`}>
        <div className="mobile-header">
          <button
            type="button"
            className="mobile-menu-button"
            onClick={handleToggleSidebar}
            aria-controls="app-sidebar"
            aria-expanded={isMobileSidebarOpen}
            aria-label={isMobileSidebarOpen ? 'Lukk meny' : 'Åpne meny'}
          >
            <span aria-hidden="true">☰</span>
          </button>
          <span className="mobile-header-brand">KI Chat</span>
        </div>
        <div className={`content-wrapper ${isFlowStudio ? 'content-wrapper--wide content-wrapper--full' : ''} ${isInquiriesPage ? 'content-wrapper--wide' : ''}`}>
          <Outlet />
        </div>
        <footer className="support-footer" role="contentinfo">
          <SupportBotEmbed />
        </footer>
      </main>
    </div>
  );
}

function LegacyInquiryDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={id ? `/henvendelser/${id}` : '/henvendelser'} replace />;
}

function App() {
  return (
    <AppErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <StatusProvider>
            <RealtimeProvider>
            <Router>
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/registrer" element={<RegisterPage />} />
            <Route
              path="/companies"
              element={(
                <RequireAuth>
                  <CompanyOverviewPage />
                </RequireAuth>
              )}
            />
            <Route
              path="/invite/company"
              element={(
                <RequireAuth>
                  <PartnerInviteCompanyPage />
                </RequireAuth>
              )}
            />
            <Route
              path="/admin/companies"
              element={(
                <RequireAuth>
                  <SuperAdminCompaniesPage />
                </RequireAuth>
              )}
            />
            <Route
              element={(
                <RequireAuth requireCompany>
                  <DashboardLayout />
                </RequireAuth>
              )}
            >
              <Route path="/" element={<HomePage />} />
              <Route path="/agent" element={<AgentPage />} />
              <Route path="/innsikt" element={<Navigate to="/chat-historikk" replace />} />
              <Route path="/innsikt/henvendelser" element={<Navigate to="/henvendelser/innboks" replace />} />
              <Route path="/innsikt/henvendelser/:id" element={<LegacyInquiryDetailRedirect />} />
              <Route path="/henvendelser" element={<HenvendelserOverviewPage />} />
              <Route path="/henvendelser/innboks" element={<InquiriesPage />} />
              <Route path="/henvendelser/oppgaver" element={<InquiryTasksPage />} />
              <Route path="/henvendelser/kalender" element={<InquiryCalendarPage />} />
              <Route path="/henvendelser/typer" element={<Navigate to="/konfigurasjon/henvendelsestyper" replace />} />
              <Route path="/henvendelser/skjema" element={<Navigate to="/konfigurasjon/skjema" replace />} />
              <Route path="/henvendelser/:id" element={<InquiryDetailPage />} />
              <Route path="/konfigurasjon" element={<Navigate to="/konfigurasjon/produkter" replace />} />
              <Route path="/konfigurasjon/produkter" element={<ProductsPage />} />
              <Route path="/konfigurasjon/produkter/ny" element={<ProductEditPage />} />
              <Route path="/konfigurasjon/produkter/:productId" element={<ProductEditPage />} />
              <Route path="/konfigurasjon/henvendelsestyper" element={<InquiryTypesPage />} />
              <Route path="/konfigurasjon/skjema" element={<FormsPage />} />
              <Route path="/selskap" element={<CompanyPage />} />
              <Route path="/selskap/medlemmer" element={<MembersPage />} />
              <Route path="/selskap/tilganger" element={<AccessPage />} />
              <Route path="/selskap/innstillinger" element={<CompanySettingsPage />} />
              <Route path="/selskap/google-calendar" element={<GoogleCalendarPage />} />
              <Route path="/selskap/tilleggsverktoy" element={<AddonsPage />} />
              {/* Tidsplaner route removed - config moved to bot Support-tidsplan tab */}
              <Route path="/selskap/abonnement" element={<Navigate to="/abonnement" replace />} />
              <Route path="/abonnement" element={<SubscriptionPage />} />
              <Route path="/bots" element={<BotsPage />} />
              <Route path="/bots/flows" element={<Navigate to="/henvendelser" replace />} />
              <Route path="/bots/flows/studio" element={<Navigate to="/henvendelser" replace />} />
              <Route path="/chat-historikk" element={<ChatHistoryPage />} />
              <Route path="/chat-flyt-oversikt" element={<Navigate to="/chat-historikk" replace />} />
              <Route path="/missions" element={<Navigate to="/chat-historikk" replace />} />
              <Route path="/chat-skjema" element={<Navigate to="/konfigurasjon/skjema" replace />} />
              {/* Installasjonsinstruksjoner moved to bot configuration */}
              {/* Kunnskapsbase moved to bot configuration */}
              <Route path="/konto/profil" element={<UserProfilePage />} />
              <Route path="/endringslogg" element={<ChangelogPage />} />
              <Route path="/integrasjoner" element={<Navigate to="/selskap/tilleggsverktoy" replace />} />
              <Route path="/booking" element={<BookingPage />} />
              <Route path="/members" element={<Navigate to="/selskap/medlemmer" replace />} />
              <Route path="/fakturering" element={<Navigate to="/abonnement" replace />} />
              <Route path="/kundeprofil" element={<Navigate to="/selskap/innstillinger" replace />} />
            </Route>
            </Routes>
            </Router>
            </RealtimeProvider>
          </StatusProvider>
        </AuthProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}

export default App;
